import { IconButton } from '@dixa/lib-shared-habla';
import { Conversation } from 'lib-shared-legacy-types';
import { useCallback, useMemo } from 'react';
import { arrayPush, arrayRemoveAll, formValueSelector } from 'redux-form';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import {
  getConversationFormCCValues,
  getConversationFormName,
  getConversationFormToValues,
} from '../../utils/conversationForm';

type ReplyAllButtonProps = {
  conversation: Conversation;
};
export const ReplyAllButton = ({
  conversation,
}: ReplyAllButtonProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const ccValues = useMemo(
    () => getConversationFormCCValues(conversation) || [],
    [conversation],
  );
  const formName = getConversationFormName(conversation.id as number);
  const selector = formValueSelector(formName);
  const formCCValue = useAppSelector((state) => selector(state, 'cc') || []);
  const formToValue = useAppSelector((state) => selector(state, 'to') || []);

  const fromEmails = useAppSelector((state) => state.composeEmail.fromEmails);
  const toValues = useMemo(
    () => getConversationFormToValues(conversation, fromEmails) || [],
    [conversation, fromEmails],
  );
  const requester = useAppSelector(
    (state) => state.conversation.metaConversation.requester,
  );

  const hasMultiplePossibleToValues = toValues.length >= 2;
  const hasPossibleCCValues = ccValues.length >= 1;
  const toUser =
    requester && toValues.find((to) => to.user.email === requester.email);

  const isReplyAll = formCCValue.length >= 2 || formToValue.length >= 2;

  const toggleReplyAll = useCallback(() => {
    // Logic to toggle reply all functionality
    // Always clean out To/CCs first
    dispatch(arrayRemoveAll(formName, 'cc'));
    dispatch(arrayRemoveAll(formName, 'to'));

    // Handle CCs
    // If we are toggling into "reply one", nothing needs to be done, field was already cleaned
    if (!isReplyAll) {
      // If we are toggling into "reply all", add all available CCs
      ccValues.forEach((cc) => {
        dispatch(arrayPush(formName, 'cc', cc));
      });
    }

    // Handle To
    if (isReplyAll) {
      // If we are toggling into "reply one", add only the requster
      if (toUser) {
        dispatch(arrayPush(formName, 'to', toUser));
      }
    } else {
      // If we are toggling into "reply all", add all available To's
      toValues?.forEach((to) => {
        dispatch(arrayPush(formName, 'to', to));
      });
    }
  }, [isReplyAll, ccValues, dispatch, formName, toValues, toUser]);

  if (!hasMultiplePossibleToValues && !hasPossibleCCValues) {
    return null;
  }

  return (
    <IconButton
      type="button"
      variant="tertiary"
      icon={!isReplyAll ? 'forward' : 'reply'}
      onClick={toggleReplyAll}
      title="Toggle reply all"
    />
  );
};
